import { Link } from "@inertiajs/react"
import { Badge, Box, Button, Card, Flex, Heading, Table, Text } from "@radix-ui/themes"
import { z } from "zod"
import { ProductSchema, ProjectSchema, PromptSchema } from "@/schemas"

const TrackedPositionSchema = z.object({
  latest_position: z.number().int().nullish(),
  ranked_at: z.string().nullish(),
})

const PromptWithTrackingSchema = PromptSchema.extend({
  tracked_positions: z.record(z.string(), TrackedPositionSchema),
})

const PropsSchema = z.object({
  project: ProjectSchema,
  tracked_products: z.array(ProductSchema),
  prompts: z.array(PromptWithTrackingSchema),
})

type Props = z.infer<typeof PropsSchema>

function positionColor(position: number): "green" | "amber" | "gray" {
  if (position <= 3) return "green"
  if (position <= 10) return "amber"
  return "gray"
}

export default function ProjectShow(rawProps: Props) {
  const props = PropsSchema.parse(rawProps)

  return (
    <Box width="100%">
      <Flex justify="between" align="start" gap="4" mb="6" wrap="wrap">
        <Box>
          <Heading size="8" mb="2">
            {props.project.name}
          </Heading>
          <Text as="p" size="3" style={{ color: "var(--gray-11)" }}>
            {props.project.description?.trim() || "Manage all prompts for this project."}
          </Text>
        </Box>

        <Button variant="classic" asChild>
          <Link href={`/projects/${props.project.id}/prompts/new`}>Create prompt</Link>
        </Button>
      </Flex>

      {props.prompts.length === 0 ? (
        <Card size="2">
          <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
            No prompts yet. Create your first prompt to get started.
          </Text>
        </Card>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          <Table.Root variant="surface" size="2">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Prompt</Table.ColumnHeaderCell>
                {props.tracked_products.map((product) => (
                  <Table.ColumnHeaderCell key={product.id}>
                    <Link href={`/projects/${props.project.id}/products/${product.id}`}>{product.name}</Link>
                  </Table.ColumnHeaderCell>
                ))}
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {props.prompts.map((prompt) => (
                <Table.Row key={prompt.id}>
                  <Table.RowHeaderCell>
                    <Link href={`/projects/${props.project.id}/prompts/${prompt.id}`}>{prompt.title}</Link>
                  </Table.RowHeaderCell>

                  {props.tracked_products.map((product) => {
                    const trackedPosition = prompt.tracked_positions[String(product.id)]
                    const latestPosition = trackedPosition?.latest_position

                    return (
                      <Table.Cell
                        key={product.id}
                        align="center"
                        title={trackedPosition?.ranked_at ? `Last update: ${new Date(trackedPosition.ranked_at).toLocaleString()}` : undefined}
                      >
                        {latestPosition ? (
                          <Badge variant="soft" color={positionColor(latestPosition)}>
                            #{latestPosition}
                          </Badge>
                        ) : (
                          <Text color="gray">-</Text>
                        )}
                      </Table.Cell>
                    )
                  })}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Box>
  )
}
